import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useSearchParams, useLocation } from "react-router-dom";
import { EVENT_DISPLAY_ORDER } from "app/utils/constant";

const EVENT_DATE_TIME_ZONE = "Asia/Kolkata";

const getDatePartsInEventTimeZone = (dateString, options = {}) => {
  if (!dateString) return [];

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return [];

  return new Intl.DateTimeFormat("en-GB", {
    timeZone: EVENT_DATE_TIME_ZONE,
    ...options,
  }).formatToParts(date);
};

export const getEventDateInIST = (dateString) => {
  const dateParts = getDatePartsInEventTimeZone(dateString, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  const day = dateParts.find((part) => part.type === "day")?.value;
  const month = dateParts.find((part) => part.type === "month")?.value;
  const year = dateParts.find((part) => part.type === "year")?.value;

  return day && month && year ? `${year}-${month}-${day}` : "";
};

export const getEventDayjsInIST = (dateString) => {
  const eventDate = getEventDateInIST(dateString);

  return eventDate ? dayjs(eventDate) : null;
};

// ordinals for dates
export const getOrdinal = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
};

// date formatting Sun, 20 Oct
export const DateFormatter = (dateString) => {
  if (!dateString) return "";

  return new Date(dateString).toLocaleDateString("en-GB", {
    timeZone: EVENT_DATE_TIME_ZONE,
    weekday: "short",
    day: "numeric",
    month: "short",
  });
};

//  date formatting as 10 Oct 2024
export const DateFormatterByDateMonthYear = (dateString) => {
  if (!dateString) return "";

  const dateParts = getDatePartsInEventTimeZone(dateString, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  const day = Number(dateParts.find((part) => part.type === "day")?.value);
  const month = dateParts.find((part) => part.type === "month")?.value;
  const year = Number(dateParts.find((part) => part.type === "year")?.value);

  const currentYear = Number(
    new Intl.DateTimeFormat("en-GB", {
      timeZone: EVENT_DATE_TIME_ZONE,
      year: "numeric",
    }).format(new Date()),
  );

  const formatted = `${day}${getOrdinal(day)} ${month}`;

  return year === currentYear ? formatted : `${formatted} ${year}`;
};

// date formatting as 20th October 2024
export const FormatDate = (dateString) => {
  if (!dateString) return "";

  const dateParts = getDatePartsInEventTimeZone(dateString, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const day = Number(dateParts.find((part) => part.type === "day")?.value);
  const month = dateParts.find((part) => part.type === "month")?.value;
  const year = dateParts.find((part) => part.type === "year")?.value;

  return `${day}${getOrdinal(day)} ${month} ${year}`;
};

export const displayTimeInIST = (utcTime, showSeconds = false) => {
  if (!utcTime) return "";

  try {
    let timePart = utcTime;

    // Extract time from ISO string
    if (utcTime.includes("T")) {
      timePart = utcTime.split("T")[1].split("Z")[0].split("+")[0];
    }

    const [hours, minutes, seconds] = timePart.split(":").map(Number);

    // Convert UTC to IST (UTC+5:30)
    let istHours = hours + 5;
    let istMinutes = minutes + 30;

    if (istMinutes >= 60) {
      istHours += 1;
      istMinutes -= 60;
    }

    istHours = istHours % 24;

    const ampm = istHours >= 12 ? "PM" : "AM";
    const formattedHours = istHours % 12 || 12;

    if (showSeconds) {
      return `${formattedHours}:${istMinutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")} ${ampm}`;
    }

    return `${formattedHours}:${istMinutes.toString().padStart(2, "0")} ${ampm}`;
  } catch (error) {
    console.error("Error displaying time:", error);
    return utcTime;
  }
};

// Time formatting UTC to IST by 12 hr for view
export const TimeFormatter = (timeString) => {
  if (!timeString) return "";

  try {
    // If it's already in 12-hour format with AM/PM, return as is
    if (timeString.includes("AM") || timeString.includes("PM")) {
      return timeString;
    }

    // If it's an ISO string, extract time part
    let timePart = timeString;
    if (timeString.includes("T")) {
      timePart = timeString.split("T")[1].split("Z")[0].split("+")[0];
    }

    // Parse hours and minutes
    const [hours, minutes] = timePart.split(":").map(Number);

    // Convert UTC to IST (UTC+5:30)
    let istHours = hours + 5;
    let istMinutes = minutes + 30;

    // Handle minute overflow
    if (istMinutes >= 60) {
      istHours += 1;
      istMinutes -= 60;
    }

    // Handle hour overflow
    istHours = istHours % 24;

    // Convert to 12-hour format
    const ampm = istHours >= 12 ? "PM" : "AM";
    const formattedHours = istHours % 12 || 12;

    return `${formattedHours}:${istMinutes.toString().padStart(2, "0")} ${ampm}`;
  } catch (error) {
    console.error("Error formatting time:", error);
    return "invalid";
  }
};

// Time Conversion -------------------------------------------

// commons.js - Update the convertISTToUTC function
export const convertISTToUTC = (timeString) => {
  if (!timeString) return "";

  try {
    // If already in 24-hour format (HH:MM:SS), return as is
    if (/^\d{2}:\d{2}(:\d{2})?$/.test(timeString.trim())) {
      return timeString.includes(":") && timeString.split(":").length === 3
        ? timeString
        : `${timeString}:00`;
    }

    // Convert 12-hour format to 24-hour format
    let time = timeString.trim().toUpperCase();
    let [timePart, ampm] = time.split(" ");

    // Handle cases where AM/PM is attached (e.g., "2:30PM")
    if (!ampm && (time.includes("AM") || time.includes("PM"))) {
      const match = time.match(/(\d{1,2}):(\d{2})(AM|PM)/i);
      if (match) {
        timePart = `${match[1]}:${match[2]}`;
        ampm = match[3];
      }
    }

    if (ampm) {
      let [hours, minutes] = timePart.split(":").map(Number);

      if (ampm === "PM" && hours !== 12) {
        hours += 12;
      } else if (ampm === "AM" && hours === 12) {
        hours = 0;
      }

      return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:00`;
    } else {
      // If no AM/PM, assume 24-hour format
      const [h, m] = timePart.split(":").map(Number);
      if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
        return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:00`;
      }
    }

    return "";
  } catch (error) {
    console.error("Error converting time:", error);
    return "";
  }
};

// UTC to IST
dayjs.extend(utc);

// commons.js - Update convertUTCToIST for display
export const convertUTCToIST = (utcTime) => {
  if (!utcTime) return "";

  try {
    let timePart = utcTime;

    // Extract time part from ISO string
    if (utcTime.includes("T")) {
      timePart = utcTime.split("T")[1].split("Z")[0].split("+")[0];
    }

    // If timePart is already in HH:MM:SS format
    const [hours, minutes, seconds] = timePart.split(":").map(Number);

    // Convert UTC to IST (UTC+5:30)
    let istHours = hours + 5;
    let istMinutes = minutes + 30;

    // Handle minute overflow
    if (istMinutes >= 60) {
      istHours += 1;
      istMinutes -= 60;
    }

    // Handle hour overflow
    istHours = istHours % 24;

    // Convert to 12-hour format
    const ampm = istHours >= 12 ? "PM" : "AM";
    const formattedHours = istHours % 12 || 12;

    return `${formattedHours}:${istMinutes.toString().padStart(2, "0")} ${ampm}`;
  } catch (error) {
    console.error("Error converting UTC to IST:", error);
    return utcTime;
  }
};

// ---------------------------------------------------------------------------------------

// product purchased === product updated
export const checkProductMatch = (productsPurchased, id) => {
  const product = productsPurchased.find((item) => item.id === id);
  const price = product?.amount / product?.quantity;

  return price;
};

// check any selected product is shippable
export const hasShippableProduct = (selectedProducts) => {
  const shippable = Object.values(selectedProducts).some(
    (product) => product && product?.product?.is_shippable === true,
  );

  return shippable;
};

// countdown timer
export const calculateCountdown = (utcDateString) => {
  const istDateString = new Date(utcDateString).toLocaleString("en-US", {
    timeZone: "Asia/Kolkata",
  });

  const targetISTDate = new Date(istDateString);
  const now = new Date();

  const diff = targetISTDate - now;

  if (diff <= 0) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      expired: true,
    };
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);

  return {
    days,
    hours,
    minutes,
    seconds,
    expired: false,
  };
};

// sum of sold items
export const SumOfSoldItems = (product, type) => {
  return product.reduce((sum, v) => {
    if (type === "hotel") {
      return (
        sum +
        (v.sold_count?.both_12_13 || 0) +
        (v.sold_count?.only_12 || 0) +
        (v.sold_count?.only_13 || 0)
      );
    } else {
      return sum + v.sold_count;
    }
  }, 0);
};

// Currency formatting
export const formatCurrency = (amount) => {
  return `₹${amount?.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

// Format date to be more readable
export const formatDate = (dateString) => {
  const utcDate = new Date(dateString + "Z");

  const options = {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };

  const istString = new Intl.DateTimeFormat("en-IN", options).format(utcDate);

  return `${istString}`;
};

// Button Labels
export const getLabels = (list, key) => {
  const item = list?.find((btn) => btn.key === key);
  return item?.label ?? item?.heading ?? "";
};

// Format labels from keys / values (ex; key_label => Key Label)
export const formatLabel = (text = "") => {
  return text
    .toString()
    .replace(/_/g, " ") // replace underscores with spaces
    .replace(/([a-z])([A-Z])/g, "$1 $2") // add space in camelCase
    .replace(/\b\w/g, (char) => char.toUpperCase()) // capitalize words
    .trim();
};

export const splitFileName = (url) => {
  return url.split("/").pop();
};

export const formatAsPayload = (text = "") => {
  return text.toLowerCase().replace(/\s+/g, "_");
};

export const normalizePhoneTo10Digits = (phone) => {
  // Remove everything except digits
  let digits = phone.replace(/\D/g, "");

  // Keep only the last 10 digits
  if (digits.length >= 10) {
    return digits.slice(-10);
  }

  return null; // invalid
};

// returns Array of objects as value and label
export const formatLists = (data = []) => {
  // Handle object map
  if (!Array.isArray(data)) {
    return Object.keys(data).map((key) => ({
      value: key,
      label: formatLabel(data[key]),
    }));
  }

  // Handle array of objects
  return data.map((item) => {
    const value = item.id ?? item.category_id ?? item.value ?? item.key ?? "";

    const label =
      item.label ||
      item.first_name ||
      item.name ||
      item.area_name ||
      formatLabel(item.value ?? item.id ?? item.key ?? "");

    return { value, label };
  });
};

export const percentageCalculation = (amount, percentage) => {
  const result = (amount * percentage) / 100;
  return result;
};

// URL Pagination Utilities

// Get current page from URL search params
export const getCurrentPageFromURL = (searchParams) => {
  const page = parseInt(searchParams.get("page"));
  return isNaN(page) || page < 1 ? 1 : page;
};

// Get rows per page from URL search params
export const getRowsPerPageFromURL = (searchParams, defaultRows = 10) => {
  const rows = parseInt(searchParams.get("rowsPerPage"));
  return isNaN(rows) || rows < 1 ? defaultRows : rows;
};

// Update page in URL search params
export const updatePageInURL = (
  searchParams,
  setSearchParams,
  newPage,
  state,
) => {
  const newSearchParams = new URLSearchParams(searchParams);
  newSearchParams.set("page", newPage.toString());
  setSearchParams(newSearchParams, { state, preventScrollReset: true, replace: true });
  return newSearchParams;
};

// Update rows per page in URL search params
export const updateRowsPerPageInURL = (
  searchParams,
  setSearchParams,
  newRowsPerPage,
  state,
) => {
  const newSearchParams = new URLSearchParams(searchParams);
  newSearchParams.set("rowsPerPage", newRowsPerPage.toString());
  newSearchParams.set("page", "1"); // Reset to first page when changing rows per page
  setSearchParams(newSearchParams, { state, preventScrollReset: true, replace: true });
  return newSearchParams;
};

// Create pagination handler for any component
export const createPaginationHandlers = (
  searchParams,
  setSearchParams,
  onSelectionClear,
  state,
) => {
  const currentPage = getCurrentPageFromURL(searchParams);
  const rowsPerPage = getRowsPerPageFromURL(searchParams);

  const handlePageChange = (newPage) => {
    if (onSelectionClear) {
      onSelectionClear();
    }
    updatePageInURL(searchParams, setSearchParams, newPage, state);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    if (onSelectionClear) {
      onSelectionClear();
    }
    updateRowsPerPageInURL(
      searchParams,
      setSearchParams,
      newRowsPerPage,
      state,
    );
  };

  return {
    currentPage,
    rowsPerPage,
    handlePageChange,
    handleRowsPerPageChange,
  };
};

// Preserve pagination when navigating to detail pages
export const preservePaginationInNavigation = (searchParams) => {
  return searchParams.toString();
};

// Get navigation URL with preserved pagination
export const getNavigationURLWithPagination = (basePath, searchParams) => {
  const queryString = searchParams.toString();
  return queryString ? `${basePath}?${queryString}` : basePath;
};

// Custom hook for pagination
export const useURLPagination = (onSelectionClear, defaultRows = 10) => {
  let clearCallback = onSelectionClear;
  let rows = defaultRows;
  if (typeof onSelectionClear === "number") {
    rows = onSelectionClear;
    clearCallback = undefined;
  }

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const currentPage = getCurrentPageFromURL(searchParams);
  const rowsPerPage = getRowsPerPageFromURL(searchParams, rows);

  const handlePageChange = (newPage) => {
    if (clearCallback && typeof clearCallback === "function") {
      clearCallback();
    }
    updatePageInURL(searchParams, setSearchParams, newPage, location.state);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    if (clearCallback && typeof clearCallback === "function") {
      clearCallback();
    }
    updateRowsPerPageInURL(
      searchParams,
      setSearchParams,
      newRowsPerPage,
      location.state,
    );
  };

  return {
    currentPage,
    rowsPerPage,
    handlePageChange,
    handleRowsPerPageChange,
    searchParams,
    setSearchParams,
  };
};

export const formatEventTypes = (obj) => {
  return EVENT_DISPLAY_ORDER.filter((key) => obj?.[key]).map((value) => ({
    value,
    label: value
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" "),
  }));
};

export const copyTextToClipboard = (text) => {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text);
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "absolute";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
  return Promise.resolve();
};